import Layout from '@/components/Layout';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { DynamicIcon } from 'lucide-react/dynamic';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ErrorDescriptionStack, TitleStack } from './styles';

interface ErrorInfo {
  code: number;
  title: string;
  message: string;
  symbol: 'ban' | 'triangle-alert' | 'cable';
  color: string;
  width: number;
}

function Failed({ code }: { code?: number }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const errors: ErrorInfo[] = useMemo(
    () => [
      {
        // not found
        code: 404,
        title: t('error.404.title'),
        message: t('error.404.message'),
        symbol: 'triangle-alert',
        width: 2.5,
        color: theme.palette.status.warning,
      },
      {
        // forbidden
        code: 403,
        title: t('error.403.title'),
        message: t('error.403.message'),
        symbol: 'ban',
        color: theme.palette.status.offline,
        width: 3,
      },
      {
        // internal server error
        code: 500,
        title: t('error.500.title'),
        message: t('error.500.message'),
        symbol: 'cable',
        color: theme.palette.status.offline,
        width: 1.5,
      },
    ],
    [t, theme]
  );

  // get code from query string and find matching error
  // fallback to first error if not found or invalid
  const currentError: ErrorInfo = useMemo(() => {
    const fallback = errors.find((err) => err.code === code) ?? errors[0];
    const raw = searchParams.get('code');
    const resolvedCode = raw ? Number.parseInt(raw, 10) : NaN;
    return errors.find((err) => err.code === resolvedCode) ?? fallback;
  }, [code, errors, searchParams]);

  return (
    <Layout noLeftMargin>
      <Stack alignItems="center" justifyContent="center" height="100%">
        <ErrorDescriptionStack>
          <TitleStack>
            <DynamicIcon
              name={currentError?.symbol}
              color={currentError?.color}
              size={80}
              strokeWidth={currentError?.width}
            />
            <Typography
              variant="h1"
              fontWeight="bold"
              color={currentError.color}
              fontSize={80}
              lineHeight={0.8}
            >
              {currentError?.code}
            </Typography>
          </TitleStack>
          <Typography variant="h2" fontWeight="bold">
            {currentError?.title}
          </Typography>
          <Typography variant="body1">{currentError?.message}</Typography>
        </ErrorDescriptionStack>
      </Stack>
    </Layout>
  );
}

export default Failed;
